import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { CheckCircle2, Loader2, Upload } from "lucide-react";
import { useGetList, useNotify, useRefresh, useTranslate } from "ra-core";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { usePapaParse } from "../misc/usePapaParse";
import type { CompanyFolder } from "../types";
import { useCompanyImport } from "./useCompanyImport";

export function CompanyImportButton() {
  const translate = useTranslate();
  const [modalOpen, setModalOpen] = useState(false);
  const handleClose = useCallback(() => setModalOpen(false), []);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <Upload />{" "}
        {translate("resources.companies.import.button", { _: "Importar CSV" })}
      </Button>
      <CompanyImportDialog open={modalOpen} onClose={handleClose} />
    </>
  );
}

function CompanyImportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: folders = [], isPending: foldersPending } =
    useGetList<CompanyFolder>(
      "company_lists",
      {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "name", order: "ASC" },
      },
      { enabled: open },
    );
  const [folderId, setFolderId] = useState<string>("");
  const selectedFolder =
    folders.find((folder) => String(folder.id) === folderId) ?? null;
  const processBatch = useCompanyImport(selectedFolder);
  const { importer, parseCsv, reset } = usePapaParse<Record<string, unknown>>({
    batchSize: 10,
    processBatch,
    dynamicTyping: false,
  });
  const [file, setFile] = useState<File | null>(null);

  const importedCount =
    importer.state === "complete" || importer.state === "running"
      ? importer.importCount
      : 0;
  const isSuccess = importer.state === "complete" && importedCount > 0;
  const isEmptyComplete = importer.state === "complete" && importedCount === 0;

  useEffect(() => {
    if (!folderId && folders.length === 1) {
      setFolderId(String(folders[0].id));
    }
  }, [folderId, folders]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    refresh();
    notify("resources.companies.import.success", {
      type: "info",
      messageArgs: {
        smart_count: importedCount,
        _: "Importación de CSV exitosa",
      },
    });
    const timeoutId = window.setTimeout(() => {
      reset();
      setFile(null);
      onClose();
    }, 1600);
    return () => window.clearTimeout(timeoutId);
  }, [importedCount, isSuccess, notify, onClose, refresh, reset]);

  const handleClose = (): void => {
    reset();
    setFile(null);
    onClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col gap-4">
          {isSuccess ? null : (
            <DialogHeader>
              <DialogTitle>
                {translate("resources.companies.import.title", {
                  _: "Importar empresas desde CSV",
                })}
              </DialogTitle>
            </DialogHeader>
          )}

          {importer.state === "running" || importer.state === "parsing" ? (
            <Alert>
              <Loader2 className="h-5 w-5 animate-spin" />
              <AlertDescription>
                {translate("resources.contacts.import.running", {
                  _: "Importando…",
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          {isSuccess ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2
                className="h-14 w-14 text-[#12B886]"
                aria-hidden="true"
              />
              <p className="text-base font-semibold">
                {translate("resources.companies.import.success", {
                  _: "Importación de CSV exitosa",
                })}
              </p>
            </div>
          ) : null}

          {isEmptyComplete || importer.state === "error" ? (
            <Alert variant="destructive">
              <AlertTitle>
                {translate("resources.companies.import.failed", {
                  _: "No se importó ninguna empresa",
                })}
              </AlertTitle>
              <AlertDescription>
                {translate("resources.companies.import.failed_help", {
                  _: "Crea una carpeta, elige esa carpeta y usa un CSV con columna nombre.",
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          {importer.state === "idle" ? (
            <>
              <p className="text-sm text-muted-foreground">
                {translate("resources.companies.import.help", {
                  _: "Primero crea la carpeta (nicho + ciudad). El CSV hereda sector y zona. No crea negocios en el pipeline.",
                })}
              </p>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="company-csv-folder"
                  className="text-sm font-medium"
                >
                  {translate("resources.companies.folders.title", {
                    _: "Carpeta",
                  })}
                </label>
                <select
                  id="company-csv-folder"
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={folderId}
                  disabled={foldersPending}
                  onChange={(event) => setFolderId(event.target.value)}
                >
                  <option value="">
                    {translate("resources.companies.folders.choose", {
                      _: "Elige una carpeta",
                    })}
                  </option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={String(folder.id)}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="company-csv-file"
                  className="text-sm font-medium"
                >
                  {translate("resources.contacts.import.csv_file", {
                    _: "Archivo CSV",
                  })}
                </label>
                <input
                  id="company-csv-file"
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground"
                  onChange={handleFileChange}
                />
                {file ? (
                  <p className="text-sm text-muted-foreground">{file.name}</p>
                ) : null}
              </div>
              <Button
                type="button"
                onClick={() => file && selectedFolder && parseCsv(file)}
                disabled={!file || !selectedFolder}
              >
                {translate("resources.companies.import.button", {
                  _: "Importar CSV",
                })}
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
